import type { Extension } from '@arcnet/contracts';
import { useSeatPhones } from '../../lib/api/hooks-extension.js';
import { Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { relativeTime } from '../../lib/format.js';
import { TeamsStatusCell } from '../manage/TeamsPresence.js';
import { deviceOf, PresenceCell, presenceOf } from './ExtensionPresence.js';

/**
 * One extension's facts beside the list (UX review, 21 October 2026, mockup C; owner chose "C + B
 * order"): what a person opens a seat to check -- why it is offline and since when, which phones it
 * has, whether Teams is signed in, how its calls are set -- without leaving the list. The editor is
 * not here: "Open extension" goes to the full page, as the list's own chevron does. The 14 September
 * objection was the editor squeezed into a third of the screen; this panel holds facts only.
 */
export interface QuickLookTeams {
  connected: boolean;
  presence: { availability: string; activity: string; at: string } | null | undefined;
}

const WHEN = (at: string) => new Date(at).toLocaleString('en-ZA', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
});

export function ExtensionQuickLook({ tenantId, ext, teams, onOpen }: {
  tenantId: string;
  ext: Extension;
  /** Set when the extension rings in Microsoft Teams. */
  teams: QuickLookTeams | null;
  /** Opens the full extension page, at one of its sections or its start. */
  onOpen: (view?: 'phone' | 'troubleshoot') => void;
}) {
  const phones = useSeatPhones(tenantId, ext.id).data;
  const p = presenceOf(ext);
  const reg = ext.registration;
  const meta = [ext.admin_group_name, ext.extension_type_name].filter(Boolean).join(' · ');
  const forwarding: Array<[string, string]> = [
    ['Always', ext.forward_always ?? ''], ['When busy', ext.forward_busy ?? ''], ['No answer', ext.forward_no_answer ?? ''],
  ].filter(([, to]) => !!to) as Array<[string, string]>;

  return (
    <aside className="arc-card arc-panel" aria-label={`Extension ${ext.number} at a glance`}>
      <div className="arc-card-body">
        <div className="arc-row" style={{ gap: 10, alignItems: 'flex-start' }}>
          <span className="arc-ext-num">{ext.number}</span>
          <div className="arc-stack" style={{ minWidth: 0, flex: 1 }}>
            <span style={{ fontWeight: 600 }} className="arc-truncate">{ext.display_name}</span>
            {meta && <span className="arc-subtle" style={{ fontSize: 12 }}>{meta}</span>}
          </div>
          {ext.do_not_disturb && <Badge tone="warn">DND</Badge>}
        </div>
      </div>

      <section className="arc-panel-section">
        <h4>Status</h4>
        <div className="arc-stack" style={{ gap: 6 }}>
          {(!teams || p.state !== 'never') && <PresenceCell ext={ext} />}
          {teams && <TeamsStatusCell connected={teams.connected} presence={teams.presence} />}
          {p.state === 'offline' && reg?.last_seen_at && (
            <span className="arc-subtle" style={{ fontSize: 12 }}>Last seen {WHEN(reg.last_seen_at)} ({relativeTime(reg.last_seen_at)})</span>
          )}
        </div>
      </section>

      <section className="arc-panel-section">
        <h4>Phones</h4>
        {phones === undefined ? <div className="arc-skeleton" style={{ height: 40 }} />
          : !phones.known ? <p className="arc-subtle" style={{ margin: 0, fontSize: 12.5 }}>No node has reported on this account yet.</p>
            : phones.data.length === 0 && !teams ? <p className="arc-subtle" style={{ margin: 0, fontSize: 12.5 }}>No phones set up yet.</p>
              : (
                <ul className="arc-stack" style={{ gap: 10, listStyle: 'none', margin: 0, padding: 0 }}>
                  {teams && (
                    <li className="arc-row" style={{ gap: 8, alignItems: 'flex-start' }}>
                      <span className="arc-muted" style={{ display: 'inline-flex', marginTop: 2 }}><Icon name="teams" size={15} /></span>
                      <div className="arc-stack" style={{ minWidth: 0 }}>
                        <span style={{ fontWeight: 550 }}>Microsoft Teams</span>
                        <span className="arc-subtle" style={{ fontSize: 12 }}>rings in Teams alongside its phones</span>
                      </div>
                    </li>
                  )}
                  {phones.data.map((ph) => {
                    const d = deviceOf(ph.user_agent);
                    const state = !ph.registered ? 'offline' : ph.reachable ? 'online' : 'unreachable';
                    return (
                      <li key={ph.username} className="arc-row" style={{ gap: 8, alignItems: 'flex-start' }}>
                        <span className="arc-seat-phone-dot" data-state={state} aria-hidden="true" style={{ marginTop: 6 }} />
                        <div className="arc-stack" style={{ minWidth: 0, gap: 1 }}>
                          <span style={{ fontWeight: 550 }}>
                            {d?.name ?? (ph.label || 'Not set up')}
                            {d?.kind ? <span className="arc-subtle" style={{ fontWeight: 400 }}> · {d.kind}</span> : null}
                          </span>
                          <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>
                            {ph.username}{ph.registered && ph.ip ? ` · ${ph.ip}` : ''}
                            {ph.registered && ph.transport ? ` · ${ph.transport.toUpperCase()}` : ''}
                            {ph.registered && ph.ping_ms !== null ? ` · ${Math.round(ph.ping_ms)} ms` : ''}
                          </span>
                          <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                            {state === 'online' ? 'online' : state === 'unreachable' ? 'registered, not answering'
                              : ph.offline_since || ph.last_seen_at ? `offline, last seen ${relativeTime(ph.offline_since ?? ph.last_seen_at!)}` : 'never registered'}
                            {ph.ring_enabled === false ? ' · does not ring' : ''}
                          </span>
                          {ph.user_agent && d && d.name !== ph.user_agent && (
                            <span className="arc-subtle arc-truncate" title={ph.user_agent} style={{ fontSize: 11 }}>{ph.user_agent}</span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
      </section>

      <section className="arc-panel-section">
        <h4>Calls</h4>
        <dl className="arc-kv">
          <dt>Do not disturb</dt><dd>{ext.do_not_disturb ? 'On' : 'Off'}</dd>
          <dt>Forwarding</dt>
          <dd>{forwarding.length === 0 ? 'Off' : forwarding.map(([when, to]) => <div key={when}>{when}: <span className="arc-mono">{to}</span></div>)}</dd>
          <dt>Caller ID</dt>
          <dd>{ext.hide_caller_id ? 'Withheld' : ext.outbound_caller_id_number
            ? <span className="arc-mono">{ext.outbound_caller_id_number}</span> : 'Account default'}</dd>
          <dt>Recording</dt><dd>{ext.record_mode === 'all' ? 'All calls' : 'Off'}</dd>
        </dl>
      </section>

      <div className="arc-panel-section arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="arc-btn arc-btn-sm" onClick={() => onOpen('phone')}>Phone setup</button>
        <button type="button" className="arc-btn arc-btn-sm" onClick={() => onOpen('troubleshoot')}>Troubleshoot</button>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-sm arc-btn-primary" onClick={() => onOpen()}>
          Open extension <Icon name="chevronRight" size={13} />
        </button>
      </div>
    </aside>
  );
}
